import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Workflow, WorkflowEmailSession } from "../../types/workflow";
import { workflowService } from "../../services/workflowService";
import { useMobile } from "../../@/hooks/use-mobile";
import WorkflowEmailDetailModal from "./WorkflowEmailDetailModal";
import { FiX, FiMail, FiRefreshCw } from "react-icons/fi";
import { formatDateTime } from "../../utils/dateFormatters";

// Import the sidebar.css which contains our brutalist design tokens
import "../../styles/sidebar.css";

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const MailIcon = FiMail as React.FC<React.SVGProps<SVGSVGElement>>;
const RefreshIcon = FiRefreshCw as React.FC<React.SVGProps<SVGSVGElement>>;

interface WorkflowEmailHistoryProps {
  workflow: Workflow;
  onClose: () => void;
}

const WorkflowEmailHistory: React.FC<WorkflowEmailHistoryProps> = ({
  workflow,
  onClose,
}) => {
  const [sessions, setSessions] = useState<WorkflowEmailSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] =
    useState<WorkflowEmailSession | null>(null);
  const [total, setTotal] = useState(0);
  const isMobile = useMobile();

  useEffect(() => {
    loadEmailSessions();
  }, [workflow.id]);

  const loadEmailSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("[EmailHistory] Loading sessions for workflow:", workflow.id, workflow.name);
      const response = await workflowService.getWorkflowEmailSessions(
        workflow.id
      );
      console.log("[EmailHistory] Response:", response);
      setSessions(response.sessions || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      console.error("Failed to load workflow email sessions:", err);
      setError(err?.message || "Failed to load email history");
    } finally {
      setLoading(false);
    }
  };


  const getStatusBadge = (session: WorkflowEmailSession) => {
    const { success_count, failure_count, total_contacts } = session;

    if (failure_count === 0 && success_count === total_contacts) {
      return (
        <span className="px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider bg-[#14532d] text-[#4ade80]">
          Completed
        </span>
      );
    } else if (success_count === 0 && failure_count > 0) {
      return (
        <span className="px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider bg-red-900/50 text-red-400">
          Failed
        </span>
      );
    } else if (failure_count > 0) {
      return (
        <span className="px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider bg-[#422006] text-[#facc15]">
          Partial
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider bg-[#1f2937] text-white/70">
        {session.status}
      </span>
    );
  };

  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-80" onClick={onClose} />

        <div className="relative z-10 flex min-h-full items-start justify-center p-3 sm:p-4">
          {/* Modal Container - using brutalist container class */}
          <div
            className="su-brutalist-container w-11/12 max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
            style={{ backgroundColor: "var(--su-brutalist-bg)" }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between p-4"
              style={{ borderBottom: "1px solid var(--su-brutalist-border)" }}
            >
              <div>
                <h3 className="text-lg font-semibold text-white">Emails Sent</h3>
                <p className="text-sm text-white/50 mt-1">
                  {workflow.name} &middot; {total} email{total !== 1 ? "s" : ""} sent
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors text-xl p-2"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
                <span className="ml-3 text-white/70">Loading email history...</span>
              </div>
            )}

            {/* Error State */}
            {!loading && error && (
              <div
                className="mx-4 mt-4 text-sm p-3"
                style={{
                  backgroundColor: "rgba(127, 29, 29, 0.3)",
                  border: "1px solid #7f1d1d",
                }}
              >
                <p className="text-red-400">{error}</p>
                <button
                  onClick={loadEmailSessions}
                  className="mt-2 text-sm text-red-400 hover:text-red-300 underline flex items-center gap-1"
                >
                  <RefreshIcon className="w-3 h-3" />
                  Try again
                </button>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && sessions.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                <div
                  className="w-16 h-16 flex items-center justify-center mb-4"
                  style={{
                    backgroundColor: "#0a0a0a",
                    border: "1px solid var(--su-brutalist-border)",
                  }}
                >
                  <MailIcon className="w-8 h-8 text-white/30" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">No emails sent yet</h4>
                <p className="text-white/50 text-sm max-w-sm">
                  This workflow has not sent any emails yet. Emails will appear here once the
                  workflow executes.
                </p>
              </div>
            )}

            {/* Email List */}
            {!loading && !error && sessions.length > 0 && (
              <div className="overflow-y-auto flex-grow p-4">
                {isMobile ? (
                  // Mobile Card Layout
                  <div className="space-y-3">
                    {sessions.map((session) => (
                      <div
                        key={session.id}
                        className="p-4 cursor-pointer transition-colors hover:bg-white/5"
                        style={{
                          backgroundColor: "#0a0a0a",
                          border: "1px solid var(--su-brutalist-border)",
                        }}
                        onClick={() => setSelectedSession(session)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-sm font-medium text-white truncate flex-1 mr-2">
                            {session.email_subject || "No Subject"}
                          </h4>
                          {getStatusBadge(session)}
                        </div>
                        <div className="text-xs text-white/50 mb-3">
                          {formatDateTime(session.created_at)}
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-center text-xs">
                          <div>
                            <div className="font-semibold text-white">
                              {session.total_contacts}
                            </div>
                            <div className="text-white/50">Total</div>
                          </div>
                          <div>
                            <div className="font-semibold text-[#4ade80]">
                              {session.success_count}
                            </div>
                            <div className="text-white/50">Sent</div>
                          </div>
                          <div>
                            <div className="font-semibold text-purple-400">
                              {session.opened_count}
                            </div>
                            <div className="text-white/50">Opened</div>
                          </div>
                          <div>
                            <div className="font-semibold text-red-400">
                              {session.failure_count}
                            </div>
                            <div className="text-white/50">Failed</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Desktop Table Layout
                  <div
                    className="overflow-x-auto"
                    style={{ border: "1px solid var(--su-brutalist-border)" }}
                  >
                    <table className="min-w-full">
                      <thead style={{ backgroundColor: "#0a0a0a" }}>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                            Subject
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                            Date Sent
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-white/60 uppercase tracking-wider">
                            Recipients
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-white/60 uppercase tracking-wider">
                            Opened
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-white/60 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.map((session, index) => (
                          <tr
                            key={session.id}
                            className="hover:bg-white/5 cursor-pointer transition-colors"
                            style={{
                              borderTop:
                                index > 0 ? "1px solid rgba(255, 255, 255, 0.08)" : undefined,
                            }}
                            onClick={() => setSelectedSession(session)}
                          >
                            <td className="px-4 py-4">
                              <div className="text-sm font-medium text-white max-w-xs truncate">
                                {session.email_subject || "No Subject"}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-white/60">
                              {formatDateTime(session.created_at)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                              <div className="text-sm">
                                <span className="text-[#4ade80] font-medium">
                                  {session.success_count}
                                </span>
                                <span className="text-white/40"> / </span>
                                <span className="text-white/70">{session.total_contacts}</span>
                                {session.failure_count > 0 && (
                                  <span className="text-red-400 ml-1">
                                    ({session.failure_count} failed)
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                              <span className="text-sm text-purple-400 font-medium">
                                {session.opened_count}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                              {getStatusBadge(session)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div
              className="p-4 flex justify-end"
              style={{ borderTop: "1px solid var(--su-brutalist-border)" }}
            >
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:bg-white/5 transition-colors"
                style={{
                  border: "1px solid var(--su-brutalist-border)",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Email Detail Modal */}
      {selectedSession && (
        <WorkflowEmailDetailModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
        />
      )}
    </>,
    document.body
  );
};

export default WorkflowEmailHistory;
